import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  Segmented,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ArrowLeftOutlined, EyeOutlined, MailOutlined, MessageOutlined } from '@ant-design/icons'
import InstitutionUserService from '../../services/InstitutionUserService'
import { isConnectivityFailure } from '../../utils/errorPopup'
import { BAILIFF_REFUND_DETAIL_KEY_ORDER } from './bailiffRefundRequestDetailConfig'

type LinkTab = 'with_link' | 'without_link'
type SourceTab = 'bidders' | 'bailiffs'

type RefundRow = Record<string, unknown> & {
  refund_request_id?: string
  bailiff_refund_request_id?: string
  reminder_email_count?: number | string
  reminder_sms_count?: number | string
  last_reminder_email_sent_at?: string | Date
  last_reminder_sms_sent_at?: string | Date
  reminder_history?: Array<Record<string, unknown>>
}

interface ReminderRouteState {
  record?: RefundRow
  linkTab?: LinkTab
}

/** Omitted from bidder reminder modal only (still on record for reminder APIs). */
const BIDDER_REFUND_DETAIL_HIDDEN_KEYS = new Set<string>([
  'refund_request_id',
  'bidder_id',
  'bid_security_id',
  'created_at',
  'updated_at',
  'otp',
  'otp_expiration',
  'bidders.bb_id',
  'bidders.bidder_id',
  'bidders.created_at',
  'bidders.institution_id',
  'bidders.updated_at',
  'bidders.user_id',
  'bid_securities.bb_id',
  'bid_securities.bs_expiration_date',
  'bid_securities.bs_id',
  'bid_securities.created_at',
  'bid_securities.doc_number',
  'bid_securities.institution_id',
  'bid_securities.payment_id',
  'bid_securities.updated_at',
  'bid_securities.used',
  'transaction_reference',
  'email',
  'phone_number',
  'amount',
  'bidders.email',
  'bidders.phone_number',
  'bid_securities.file',
  'bid_securities.type'
])

const DETAIL_KEY_ORDER: string[] = [
  'bailiff_refund_request_id',
  'status',
  'bailiff_email',
  'bailiff_phone_number',
  'link',
  'bank_code',
  'debit_account_number',
  'debit_account_name',
  'beneficiary_account_name',
  'beneficiary_bank_name',
  'beneficiary_account_number',
  'transaction_currency',
  'reason',
  'property_id',
  'execution_case_number',
  'bailiff_assignee',
  'bailiff_national_id',
  'created_by',
  'attachment'
]

const SKIP_ROOT_KEYS = new Set([
  'bidders',
  'bid_securities',
  'auditLogs',
  'reminder_email_count',
  'reminder_sms_count',
  'last_reminder_email_sent_at',
  'last_reminder_sms_sent_at',
  'reminder_history'
])

const TABLE_SCROLL_WIDTH = 1080
const TABLE_RIGHT_GUTTER = 32

const pageLayoutStyle: React.CSSProperties = {
  paddingRight: TABLE_RIGHT_GUTTER,
  boxSizing: 'border-box'
}

const tableAreaStyle: React.CSSProperties = {
  marginRight: TABLE_RIGHT_GUTTER,
  maxWidth: `calc(100% - ${TABLE_RIGHT_GUTTER}px)`
}

const heroCardStyle: React.CSSProperties = {
  border: '1px solid #d8ead5',
  borderRadius: 20,
  background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 58%, #f8fafc 100%)',
  boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)',
  overflow: 'hidden'
}

const workspaceCardStyle: React.CSSProperties = {
  ...tableAreaStyle,
  borderRadius: 18,
  boxShadow: '0 14px 35px rgba(15, 23, 42, 0.07)'
}

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  gap: 16,
  margin: '18px 0',
  padding: 16,
  border: '1px solid #e5e7eb',
  borderRadius: 16,
  background: '#f8fafc',
  flexWrap: 'wrap'
}

const statGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
  margin: '16px 0 6px'
}

const statCardStyle: React.CSSProperties = {
  padding: 16,
  border: '1px solid #e5e7eb',
  borderRadius: 16,
  background: '#ffffff',
  boxShadow: '0 8px 20px rgba(15, 23, 42, 0.05)'
}

const modalTitleIconStyle: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 14,
  background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
  color: '#ffffff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 800,
  boxShadow: '0 10px 22px rgba(55, 118, 47, 0.24)'
}

const modalIntroStyle: React.CSSProperties = {
  padding: 16,
  border: '1px solid #d8ead5',
  borderRadius: 16,
  background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)',
  marginBottom: 14
}

const reminderSummaryGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 12,
  marginBottom: 16
}

const reminderMetricCardStyle: React.CSSProperties = {
  padding: 16,
  border: '1px solid #e5e7eb',
  borderRadius: 16,
  background: '#ffffff',
  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)'
}

const detailActionBarStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
  marginTop: 18,
  paddingTop: 16,
  borderTop: '1px solid #e5e7eb'
}

const reminderActionButtonStyle: React.CSSProperties = {
  minWidth: 240,
  height: 44,
  borderRadius: 12,
  background: '#37762f',
  borderColor: '#37762f',
  color: '#ffffff',
  fontWeight: 700,
  opacity: 1
}

interface DetailDescriptionRow {
  key: string
  label: React.ReactNode
  children: React.ReactNode
}

/** Matches server rule: TRIM(COALESCE(link,'')) — null, '', or whitespace-only = no link. */
function hasMeaningfulRefundLink(link: unknown): boolean {
  if (link == null) {
    return false
  }
  return String(link).trim() !== ''
}

function normalizeReminderCount(value: unknown): number {
  const count = Number(value ?? 0)
  return Number.isFinite(count) && count > 0 ? count : 0
}

function renderReminderCount(value: unknown): React.ReactNode {
  const count = normalizeReminderCount(value)
  return <Tag color={count > 0 ? 'green' : 'default'}>{count}</Tag>
}

function renderReminderSummary(_: unknown, record: RefundRow): React.ReactNode {
  const emailCount = normalizeReminderCount(record.reminder_email_count)
  const smsCount = normalizeReminderCount(record.reminder_sms_count)
  return (
    <Space direction="vertical" size={4}>
      <Tag color={emailCount > 0 ? 'blue' : 'default'} icon={<MailOutlined />}>
        Email: {emailCount}
      </Tag>
      <Tag color={smsCount > 0 ? 'purple' : 'default'} icon={<MessageOutlined />}>
        SMS: {smsCount}
      </Tag>
    </Space>
  )
}

function formatReminderTimestamp(value: unknown): string {
  if (value == null || value === '') {
    return 'Not sent yet'
  }
  const date = value instanceof Date ? value : new Date(String(value))
  if (Number.isNaN(date.getTime())) {
    return String(value)
  }
  return date.toLocaleString()
}

function getRefundRowId(record: RefundRow, source: SourceTab): string {
  const id =
    source === 'bidders'
      ? record.refund_request_id
      : record.bailiff_refund_request_id
  return String(id ?? '').trim()
}

function humanizeField(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatDetailValue(key: string, value: unknown): React.ReactNode {
  if (value == null || value === '') {
    return '—'
  }
  if (typeof value === 'object') {
    return (
      <Typography.Text code copyable>
        {JSON.stringify(value)}
      </Typography.Text>
    )
  }
  const str = String(value)
  if (key === 'link' && hasMeaningfulRefundLink(str)) {
    const href = str.trim()
    return (
      <Typography.Link href={href} target="_blank" rel="noreferrer">
        {href.length > 80 ? `${href.slice(0, 80)}…` : href}
      </Typography.Link>
    )
  }
  if (str.length > 400) {
    return (
      <Typography.Paragraph style={{ marginBottom: 0 }} ellipsis={{ rows: 3, expandable: true }}>
        {str}
      </Typography.Paragraph>
    )
  }
  return str
}

function buildDetailDescriptionRows(
  record: RefundRow | null,
  keyOrder: readonly string[] = DETAIL_KEY_ORDER,
  hiddenKeys: ReadonlySet<string> = new Set()
): DetailDescriptionRow[] {
  if (record == null) {
    return []
  }
  const remaining = new Set(
    Object.keys(record).filter((k) => !SKIP_ROOT_KEYS.has(k) && !hiddenKeys.has(k))
  )
  const orderedKeys: string[] = []
  for (const k of keyOrder) {
    if (remaining.has(k)) {
      orderedKeys.push(k)
      remaining.delete(k)
    }
  }
  const rest = Array.from(remaining).sort()
  const keys = [...orderedKeys, ...rest]

  const rows: DetailDescriptionRow[] = keys.map((k) => ({
    key: k,
    label: humanizeField(k),
    children: formatDetailValue(k, record[k])
  }))

  for (const nest of ['bidders', 'bid_securities'] as const) {
    const sub = record[nest]
    if (sub != null && typeof sub === 'object' && !Array.isArray(sub)) {
      const o = sub as Record<string, unknown>
      for (const k of Object.keys(o).sort()) {
        const compoundKey = `${nest}.${k}`
        if (hiddenKeys.has(compoundKey)) {
          continue
        }
        rows.push({
          key: compoundKey,
          label: `${humanizeField(nest)} ${humanizeField(k)}`,
          children: formatDetailValue(k, o[k])
        })
      }
    }
  }

  return rows
}

const PendingRefundReminders: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { reminderSource, transactionReference } = useParams()
  const routeSource: SourceTab | null =
    reminderSource === 'bidders' || reminderSource === 'bailiffs'
      ? reminderSource
      : null
  const isDetailPage = routeSource != null && transactionReference != null
  const decodedTransactionReference =
    transactionReference != null ? decodeURIComponent(transactionReference) : ''
  const routeState = (location.state ?? {}) as ReminderRouteState
  const initialLinkTab: LinkTab =
    routeState.linkTab === 'without_link' ? 'without_link' : 'with_link'

  const [source, setSource] = useState<SourceTab>(routeSource ?? 'bidders')
  const [linkTab, setLinkTab] = useState<LinkTab>(initialLinkTab)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [bidderRows, setBidderRows] = useState<RefundRow[]>([])
  const [bailiffRows, setBailiffRows] = useState<RefundRow[]>([])
  const [listPager, setListPager] = useState({ current: 1, pageSize: 10 })
  const [bidderTotal, setBidderTotal] = useState(0)
  const [bailiffTotal, setBailiffTotal] = useState(0)

  const [detailOpen, setDetailOpen] = useState(isDetailPage)
  const [detailRecord, setDetailRecord] = useState<RefundRow | null>(
    isDetailPage && routeState.record != null ? routeState.record : null
  )
  const [detailSource, setDetailSource] = useState<SourceTab | null>(routeSource)
  const [loadingEmail, setLoadingEmail] = useState(false)
  const [loadingSms, setLoadingSms] = useState(false)
  /**
   * When set, inline detail panel loads full `bailiff_refund_request`
   * via view-by-reference (same data path as other bailiff refund screens).
   */
  const [bailiffDetailRef, setBailiffDetailRef] = useState<string | null>(
    isDetailPage && routeSource === 'bailiffs' ? decodedTransactionReference : null
  )
  const [bailiffDetailLoading, setBailiffDetailLoading] = useState(false)

  const page = listPager.current
  const pageSize = listPager.pageSize

  const fetchPendingLists = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      const searchArg = search.trim() !== '' ? search.trim() : undefined
      // Bidder rows: RefundRequest. Bailiff rows: BailiffRefundRequest (bailiff_refund_request).
      // Sequential requests — shared axios cancel token would abort the first if both ran in parallel.
      const bidRes: any =
        await InstitutionUserService.GetPendingRefundRequestListByStatusFromBidders(
          pageSize,
          page,
          searchArg,
          undefined,
          undefined,
          linkTab
        )
      if (bidRes?.status === 200) {
        setBidderRows(Array.isArray(bidRes.data) ? bidRes.data : [])
        setBidderTotal(bidRes.meta?.total ?? 0)
      } else {
        setBidderRows([])
        setBidderTotal(0)
      }

      const bailRes: any =
        await InstitutionUserService.GetPendingRefundRequestListByStatusFromBailiffs(
          pageSize,
          page,
          searchArg,
          undefined,
          undefined,
          linkTab
        )
      if (bailRes?.status === 200) {
        setBailiffRows(Array.isArray(bailRes.data) ? bailRes.data : [])
        setBailiffTotal(bailRes.meta?.total ?? 0)
      } else {
        setBailiffRows([])
        setBailiffTotal(0)
      }
    } catch (e: any) {
      if (e?.message === 'Operation canceled due to new request.') {
        // Keep current rows; a newer list request is in flight.
      } else {
        if (!isConnectivityFailure(e)) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- antd message API
          message.error(e?.message ?? 'Failed to load pending requests')
        }
        setBidderRows([])
        setBailiffRows([])
        setBidderTotal(0)
        setBailiffTotal(0)
      }
    } finally {
      setLoading(false)
    }
  }, [linkTab, search, page, pageSize])

  useEffect(() => {
    if (isDetailPage) {
      return
    }
    fetchPendingLists().catch(() => {
      // Errors are reported inside fetchPendingLists
    })
  }, [fetchPendingLists, isDetailPage])

  const openDetailModal = useCallback((record: RefundRow, src: SourceTab) => {
    const reference = String(record.transaction_reference ?? '').trim()
    if (reference === '') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- antd message API
      message.warning('Transaction reference is missing for this request.')
      return
    }
    navigate(`/pending-refund-reminders/${src}/${encodeURIComponent(reference)}`, {
      state: { record, linkTab }
    })
  }, [linkTab, navigate])

  const closeDetailModal = useCallback(() => {
    if (isDetailPage) {
      navigate('/pending-refund-reminders')
      return
    }
    setDetailOpen(false)
    setDetailRecord(null)
    setDetailSource(null)
    setBailiffDetailRef(null)
    setBailiffDetailLoading(false)
    setLoadingEmail(false)
    setLoadingSms(false)
  }, [isDetailPage, navigate])

  useEffect(() => {
    if (!isDetailPage || routeSource == null) {
      return
    }
    setDetailOpen(true)
    setDetailSource(routeSource)
    setSource(routeSource)
    setDetailRecord(routeState.record ?? null)
    setBailiffDetailRef(
      routeSource === 'bailiffs' ? decodedTransactionReference : null
    )
  }, [decodedTransactionReference, isDetailPage, routeSource])

  useEffect(() => {
    if (
      !isDetailPage ||
      routeSource !== 'bidders' ||
      decodedTransactionReference === '' ||
      detailRecord != null
    ) {
      return
    }

    let cancelled = false
    const filters: LinkTab[] =
      linkTab === 'with_link' ? ['with_link', 'without_link'] : ['without_link', 'with_link']

    setBailiffDetailLoading(true)
    void (async () => {
      try {
        for (const filter of filters) {
          const res: any =
            await InstitutionUserService.GetPendingRefundRequestListByStatusFromBidders(
              10,
              1,
              decodedTransactionReference,
              undefined,
              undefined,
              filter
            )
          if (cancelled) {
            return
          }
          const rows = Array.isArray(res?.data) ? res.data : []
          const match = rows.find((row: RefundRow) =>
            String(row.transaction_reference ?? '').trim() === decodedTransactionReference
          ) ?? rows[0]
          if (match != null) {
            setDetailRecord(match)
            return
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- antd message API
        message.warning('Could not load the selected bidder refund request.')
      } catch (e: any) {
        if (!cancelled && !isConnectivityFailure(e)) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- antd message API
          message.error(e?.message ?? 'Failed to load bidder refund details')
        }
      } finally {
        if (!cancelled) {
          setBailiffDetailLoading(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [decodedTransactionReference, detailRecord, isDetailPage, linkTab, routeSource])

  useEffect(() => {
    const bailiffRefMissing =
      bailiffDetailRef == null || bailiffDetailRef === ''
    if (!detailOpen || detailSource !== 'bailiffs' || bailiffRefMissing) {
      return
    }
    let cancelled = false
    setBailiffDetailLoading(true)
    void (async () => {
      try {
        const res: any = await InstitutionUserService.viewBailiffRefundRequestByReferenceNumber(
          bailiffDetailRef
        )
        if (cancelled) {
          return
        }
        if (res?.status === 200) {
          const raw = res.data
          const fetched = Array.isArray(raw) ? raw[0] : raw
          if (fetched != null && typeof fetched === 'object') {
            setDetailRecord((prev) => {
              if (prev == null) {
                return fetched as Record<string, unknown>
              }
              if (String(prev.transaction_reference ?? '').trim() !== bailiffDetailRef) {
                return prev
              }
              return { ...prev, ...(fetched as Record<string, unknown>) }
            })
          }
        } else {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- antd message API
          message.warning(res?.message ?? 'Could not load full bailiff refund request from server')
        }
      } catch (e: any) {
        if (!cancelled && !isConnectivityFailure(e)) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- antd message API
          message.error(e?.message ?? 'Failed to load bailiff refund details')
        }
      } finally {
        if (!cancelled) {
          setBailiffDetailLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [detailOpen, detailSource, bailiffDetailRef])

  const detailDescriptionRows = useMemo(
    () =>
      buildDetailDescriptionRows(
        detailRecord,
        detailSource === 'bailiffs' ? BAILIFF_REFUND_DETAIL_KEY_ORDER : DETAIL_KEY_ORDER,
        detailSource === 'bidders' ? BIDDER_REFUND_DETAIL_HIDDEN_KEYS : new Set<string>()
      ),
    [detailRecord, detailSource]
  )

  const emailOnRecord = useMemo(() => {
    if (detailRecord == null) {
      return ''
    }
    const raw =
      detailSource === 'bidders'
        ? detailRecord.email
        : detailRecord.bailiff_email
    return String(raw ?? '').trim()
  }, [detailRecord, detailSource])

  const phoneOnRecord = useMemo(() => {
    if (detailRecord == null) {
      return ''
    }
    const raw =
      detailSource === 'bidders'
        ? detailRecord.phone_number
        : detailRecord.bailiff_phone_number
    return String(raw ?? '').trim()
  }, [detailRecord, detailSource])

  const detailEmailReminderCount = normalizeReminderCount(detailRecord?.reminder_email_count)
  const detailSmsReminderCount = normalizeReminderCount(detailRecord?.reminder_sms_count)
  const activeRows = source === 'bidders' ? bidderRows : bailiffRows
  const activeTotal = source === 'bidders' ? bidderTotal : bailiffTotal
  const activeSourceLabel = source === 'bidders' ? 'Bidder requests' : 'Bailiff requests'
  const emailRemindersOnPage = activeRows.reduce(
    (sum, row) => sum + normalizeReminderCount(row.reminder_email_count),
    0
  )
  const smsRemindersOnPage = activeRows.reduce(
    (sum, row) => sum + normalizeReminderCount(row.reminder_sms_count),
    0
  )

  const applyReminderSendResult = useCallback((
    channel: 'email' | 'sms',
    responseData?: Record<string, unknown>
  ): void => {
    if (detailRecord == null || detailSource == null) {
      return
    }

    const rowId = getRefundRowId(detailRecord, detailSource)
    if (rowId === '') {
      return
    }

    const countKey =
      channel === 'email' ? 'reminder_email_count' : 'reminder_sms_count'
    const sentAtKey =
      channel === 'email' ? 'last_reminder_email_sent_at' : 'last_reminder_sms_sent_at'
    const sentAt = responseData?.[sentAtKey] ?? new Date().toISOString()
    const responseReminderHistory = responseData?.reminder_history

    const updateRow = (row: RefundRow): RefundRow => {
      const fallbackCount = normalizeReminderCount(row[countKey]) + 1
      return {
        ...row,
        [countKey]: normalizeReminderCount(responseData?.[countKey] ?? fallbackCount),
        [sentAtKey]: sentAt,
        ...(Array.isArray(responseReminderHistory)
          ? { reminder_history: responseReminderHistory }
          : {})
      }
    }

    setDetailRecord((prev) =>
      prev != null && getRefundRowId(prev, detailSource) === rowId
        ? updateRow(prev)
        : prev
    )

    if (detailSource === 'bidders') {
      setBidderRows((rows) =>
        rows.map((row) => getRefundRowId(row, detailSource) === rowId ? updateRow(row) : row)
      )
    } else {
      setBailiffRows((rows) =>
        rows.map((row) => getRefundRowId(row, detailSource) === rowId ? updateRow(row) : row)
      )
    }
  }, [detailRecord, detailSource])

  const sendReminderEmail = useCallback(async (): Promise<void> => {
    if (detailRecord == null || detailSource == null) {
      return
    }
    setLoadingEmail(true)
    try {
      let res: any
      if (detailSource === 'bidders') {
        const id = String(detailRecord.refund_request_id ?? '')
        const withLinkCopy = linkTab === 'with_link'
        const noLinkCopy = linkTab === 'without_link'
        res = await InstitutionUserService.SendPendingBidderRefundReminderEmail({
          refund_request_id: id,
          ...(withLinkCopy ? { use_with_link_reminder_copy: true } : {}),
          ...(noLinkCopy ? { use_no_link_reminder_copy: true } : {})
        })
      } else {
        const id = String(detailRecord.bailiff_refund_request_id ?? '')
        res = await InstitutionUserService.SendPendingBailiffRefundReminderEmail({
          bailiff_refund_request_id: id
        })
      }
      if (res?.status === 200) {
        applyReminderSendResult('email', res?.data)
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- antd message API
        message.success(res?.message ?? 'Reminder email sent')
      } else {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- antd message API
        message.warning(res?.message ?? 'Could not send email')
      }
    } catch (e: any) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- antd message API
      message.error(e?.message ?? 'Failed to send email')
    } finally {
      setLoadingEmail(false)
    }
  }, [applyReminderSendResult, detailRecord, detailSource, linkTab])

  const sendReminderSms = useCallback(async (): Promise<void> => {
    if (detailRecord == null || detailSource == null) {
      return
    }
    setLoadingSms(true)
    try {
      let res: any
      if (detailSource === 'bidders') {
        const id = String(detailRecord.refund_request_id ?? '')
        const withLinkCopy = linkTab === 'with_link'
        const noLinkCopy = linkTab === 'without_link'
        res = await InstitutionUserService.SendPendingBidderRefundReminderSms({
          refund_request_id: id,
          ...(withLinkCopy ? { use_with_link_reminder_copy: true } : {}),
          ...(noLinkCopy ? { use_no_link_reminder_copy: true } : {})
        })
      } else {
        const id = String(detailRecord.bailiff_refund_request_id ?? '')
        res = await InstitutionUserService.SendPendingBailiffRefundReminderSms({
          bailiff_refund_request_id: id
        })
      }
      if (res?.status === 200) {
        applyReminderSendResult('sms', res?.data)
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- antd message API
        message.success(res?.message ?? 'Reminder SMS sent')
      } else {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- antd message API
        message.warning(res?.message ?? 'Could not send SMS')
      }
    } catch (e: any) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- antd message API
      message.error(e?.message ?? 'Failed to send SMS')
    } finally {
      setLoadingSms(false)
    }
  }, [applyReminderSendResult, detailRecord, detailSource, linkTab])

  const bidderColumns: ColumnsType<RefundRow> = useMemo(
    () => [
      {
        title: 'Transaction ref.',
        dataIndex: 'transaction_reference',
        key: 'transaction_reference',
        ellipsis: true,
        width: 140
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        ellipsis: true,
        width: 200
      },
      {
        title: 'Refund link',
        dataIndex: 'link',
        key: 'link',
        width: 220,
        render: (v: unknown) => {
          if (!hasMeaningfulRefundLink(v)) {
            return <Tag color="default">None</Tag>
          }
          const s = String(v).trim()
          return (
            <Tooltip title={s}>
              <Typography.Link href={s} target="_blank" rel="noreferrer">
                {s.length > 40 ? `${s.slice(0, 40)}…` : s}
              </Typography.Link>
            </Tooltip>
          )
        }
      },
      {
        title: 'Reminders',
        key: 'reminders',
        width: 180,
        render: renderReminderSummary
      },
      {
        title: 'Updated',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 160,
        ellipsis: true
      },
      {
        title: 'Action',
        key: 'action',
        width: 180,
        fixed: 'right',
        render: (_: unknown, record) => (
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              openDetailModal(record, 'bidders')
            }}
          >
            View & remind
          </Button>
        )
      }
    ],
    [openDetailModal]
  )

  const bailiffColumns: ColumnsType<RefundRow> = useMemo(
    () => [
      {
        title: 'Transaction ref.',
        dataIndex: 'transaction_reference',
        key: 'transaction_reference',
        ellipsis: true,
        width: 140
      },
      {
        title: 'Bailiff email',
        dataIndex: 'bailiff_email',
        key: 'bailiff_email',
        ellipsis: true,
        width: 200
      },
      {
        title: 'Refund link',
        dataIndex: 'link',
        key: 'link',
        width: 220,
        render: (v: unknown) => {
          if (!hasMeaningfulRefundLink(v)) {
            return <Tag color="default">None</Tag>
          }
          const s = String(v).trim()
          return (
            <Tooltip title={s}>
              <Typography.Link href={s} target="_blank" rel="noreferrer">
                {s.length > 40 ? `${s.slice(0, 40)}…` : s}
              </Typography.Link>
            </Tooltip>
          )
        }
      },
      {
        title: 'Reminders',
        key: 'reminders',
        width: 180,
        render: renderReminderSummary
      },
      {
        title: 'Updated',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 160,
        ellipsis: true
      },
      {
        title: 'Action',
        key: 'action',
        width: 180,
        fixed: 'right',
        render: (_: unknown, record) => (
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              openDetailModal(record, 'bailiffs')
            }}
          >
            View & remind
          </Button>
        )
      }
    ],
    [openDetailModal]
  )

  const modalSourceTitle =
    detailSource === 'bidders'
      ? 'Bidder refund request'
      : detailSource === 'bailiffs'
        ? 'Bailiff refund request'
        : 'Refund request'
  const modalTitleContent = (
    <Space align="start" size={12}>
      <span style={modalTitleIconStyle}>
        {detailSource === 'bailiffs' ? 'BR' : 'RR'}
      </span>
      <Space direction="vertical" size={0}>
        <Typography.Title level={4} style={{ margin: 0, color: '#1f2937' }}>
          {modalSourceTitle}
        </Typography.Title>
        <Typography.Text type="secondary">
          Review request details and send the right follow-up reminder.
        </Typography.Text>
      </Space>
    </Space>
  )

  return (
    <div
      className="p-4 md:p-6 max-w-[1280px] mx-auto"
      style={pageLayoutStyle}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card style={heroCardStyle} bodyStyle={{ padding: 24 }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              style={{ color: '#37762f', fontWeight: 600, paddingLeft: 0 }}
              onClick={() => {
                navigate('/dashboard')
              }}
            >
              Back to dashboard
            </Button>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                gap: 24,
                flexWrap: 'wrap'
              }}
            >
              <div style={{ maxWidth: 720 }}>
                <Typography.Title level={2} style={{ margin: 0, color: '#111827' }}>
                  Pending refund reminders
                </Typography.Title>
                <Typography.Paragraph
                  type="secondary"
                  style={{ marginTop: 8, marginBottom: 12, fontSize: 15 }}
                >
                  Review pending bidder and bailiff refund requests, confirm whether a refund
                  link exists, and send follow-up email or SMS reminders from one workspace.
                </Typography.Paragraph>
                <Space wrap>
                  <Tag color="green">Status: PENDING</Tag>
                  <Tag color={source === 'bidders' ? 'blue' : 'purple'}>
                    {activeSourceLabel}
                  </Tag>
                  <Tag color={linkTab === 'with_link' ? 'success' : 'warning'}>
                    {linkTab === 'with_link' ? 'With valid link' : 'No link'}
                  </Tag>
                </Space>
              </div>
            </div>
          </Space>
        </Card>

        {!isDetailPage && (
          <Card
            title={
              <Space direction="vertical" size={0}>
                <Typography.Text strong>Reminder workspace</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Filter, review, and send reminders for pending refunds.
                </Typography.Text>
              </Space>
            }
            className="app-table"
            style={workspaceCardStyle}
            extra={
              <Tag color="green">PENDING only</Tag>
            }
          >
          <Tabs
            activeKey={source}
            onChange={(k) => {
              setSource(k as SourceTab)
              setListPager((p) => ({ ...p, current: 1 }))
            }}
            items={[
              { key: 'bidders', label: 'Bidder refund requests' },
              { key: 'bailiffs', label: 'Bailiff refund requests' }
            ]}
          />

          <div style={statGridStyle}>
            <div style={statCardStyle}>
              <Typography.Text type="secondary">Matching pending requests</Typography.Text>
              <Typography.Title level={3} style={{ margin: '4px 0 0', color: '#37762f' }}>
                {activeTotal}
              </Typography.Title>
            </div>
            <div style={statCardStyle}>
              <Typography.Text type="secondary">Rows on this page</Typography.Text>
              <Typography.Title level={3} style={{ margin: '4px 0 0' }}>
                {activeRows.length}
              </Typography.Title>
            </div>
            <div style={statCardStyle}>
              <Typography.Text type="secondary">Email reminders on page</Typography.Text>
              <Typography.Title level={3} style={{ margin: '4px 0 0', color: '#1677ff' }}>
                {emailRemindersOnPage}
              </Typography.Title>
            </div>
            <div style={statCardStyle}>
              <Typography.Text type="secondary">SMS reminders on page</Typography.Text>
              <Typography.Title level={3} style={{ margin: '4px 0 0', color: '#722ed1' }}>
                {smsRemindersOnPage}
              </Typography.Title>
            </div>
          </div>

          <div style={toolbarStyle}>
            <div style={{ flex: '1 1 420px', minWidth: 280 }}>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                Refund link on record
              </Typography.Text>
              <Segmented
                block
                size="large"
                value={linkTab}
                onChange={(v) => {
                  setLinkTab(v as LinkTab)
                  setListPager((p) => ({ ...p, current: 1 }))
                }}
                options={[
                  { label: 'With valid link', value: 'with_link' },
                  { label: 'No link', value: 'without_link' }
                ]}
              />
              <Alert
                style={{ marginTop: 12, borderRadius: 12 }}
                type="info"
                showIcon
                message={
                  linkTab === 'with_link'
                    ? 'Showing requests where a usable refund link is already stored.'
                    : 'Showing requests where the link is missing, empty, or whitespace only.'
                }
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 8,
                flex: '1 1 360px',
                flexWrap: 'wrap'
              }}
            >
              <Input.Search
                allowClear
                placeholder="Search by transaction reference…"
                onChange={(e) => {
                  if (e.target.value === '') {
                    setSearch('')
                    setListPager((p) => ({ ...p, current: 1 }))
                  }
                }}
                onSearch={(v) => {
                  setSearch(String(v ?? '').trim())
                  setListPager((p) => ({ ...p, current: 1 }))
                }}
                style={{ width: '100%', maxWidth: 420 }}
              />
              <Button
                onClick={() => {
                  fetchPendingLists().catch(() => {
                    // Errors surfaced inside fetchPendingLists
                  })
                }}
                loading={loading}
              >
                Refresh
              </Button>
            </div>
          </div>

          {source === 'bidders'
            ? (
              <>
                <Table<RefundRow>
                rowKey="refund_request_id"
                className="app-table"
                bordered
                size="middle"
                loading={loading}
                columns={bidderColumns}
                dataSource={bidderRows}
                rowClassName={(_, index) =>
                  index % 2 === 0 ? 'app-table-row-even' : 'app-table-row-odd'}
                scroll={{ x: TABLE_SCROLL_WIDTH }}
                pagination={{
                  current: listPager.current,
                  pageSize: listPager.pageSize,
                  total: bidderTotal,
                  showSizeChanger: true,
                  onChange: (nextPage, nextPageSize) => {
                    setListPager((p) => ({
                      current: nextPage,
                      pageSize: nextPageSize ?? p.pageSize
                    }))
                  }
                }}
              />
              </>
              )
            : (
              <>
                <Table<RefundRow>
                rowKey="bailiff_refund_request_id"
                className="app-table"
                bordered
                size="middle"
                loading={loading}
                columns={bailiffColumns}
                dataSource={bailiffRows}
                rowClassName={(_, index) =>
                  index % 2 === 0 ? 'app-table-row-even' : 'app-table-row-odd'}
                scroll={{ x: TABLE_SCROLL_WIDTH }}
                pagination={{
                  current: listPager.current,
                  pageSize: listPager.pageSize,
                  total: bailiffTotal,
                  showSizeChanger: true,
                  onChange: (nextPage, nextPageSize) => {
                    setListPager((p) => ({
                      current: nextPage,
                      pageSize: nextPageSize ?? p.pageSize
                    }))
                  }
                }}
              />
              </>
              )}
          </Card>
        )}

        {isDetailPage && detailRecord == null && (
          <Card style={workspaceCardStyle}>
            <Spin spinning>
              <Typography.Text>Loading selected refund request...</Typography.Text>
            </Spin>
          </Card>
        )}

        {isDetailPage && detailOpen && detailRecord != null && (
          <Card
            title={modalTitleContent}
            style={workspaceCardStyle}
            extra={<Button onClick={closeDetailModal}>Close details</Button>}
          >
            <div style={modalIntroStyle}>
              <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                Reminder action center
              </Typography.Text>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 10 }}>
                {detailSource === 'bailiffs'
                  ? 'Bailiff details load from the bailiff refund request record, then reminders can be sent by email or SMS.'
                  : 'Review the refund request details, confirm the available contacts, then send an email or SMS reminder.'}
              </Typography.Paragraph>
              <Space wrap>
                <Tag color={detailSource === 'bidders' ? 'blue' : 'purple'}>
                  {modalSourceTitle}
                </Tag>
                <Tag color="green">Status: PENDING</Tag>
                <Tag color={emailOnRecord !== '' ? 'blue' : 'default'} icon={<MailOutlined />}>
                  {emailOnRecord !== '' ? 'Email available' : 'No email'}
                </Tag>
                <Tag color={phoneOnRecord !== '' ? 'purple' : 'default'} icon={<MessageOutlined />}>
                  {phoneOnRecord !== '' ? 'Phone available' : 'No phone'}
                </Tag>
              </Space>
            </div>

            <div style={reminderSummaryGridStyle}>
              <div style={reminderMetricCardStyle}>
                <Space align="center" size={8}>
                  <MailOutlined style={{ color: '#1677ff' }} />
                  <Typography.Text strong>Email reminders</Typography.Text>
                </Space>
                <Typography.Title level={3} style={{ margin: '8px 0 0', color: '#1677ff' }}>
                  {detailEmailReminderCount}
                </Typography.Title>
                <Typography.Text type="secondary">
                  Last sent: {formatReminderTimestamp(detailRecord?.last_reminder_email_sent_at)}
                </Typography.Text>
              </div>
              <div style={reminderMetricCardStyle}>
                <Space align="center" size={8}>
                  <MessageOutlined style={{ color: '#722ed1' }} />
                  <Typography.Text strong>SMS reminders</Typography.Text>
                </Space>
                <Typography.Title level={3} style={{ margin: '8px 0 0', color: '#722ed1' }}>
                  {detailSmsReminderCount}
                </Typography.Title>
                <Typography.Text type="secondary">
                  Last sent: {formatReminderTimestamp(detailRecord?.last_reminder_sms_sent_at)}
                </Typography.Text>
              </div>
            </div>

            <Spin spinning={detailSource === 'bailiffs' && bailiffDetailLoading}>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                Request details
              </Typography.Text>
              <div style={{ maxHeight: 'min(70vh, 620px)', overflowY: 'auto' }}>
                <Descriptions bordered size="small" column={{ xs: 1, sm: 1, md: 2 }}>
                  {detailDescriptionRows.map(({ key, label, children }) => (
                    <Descriptions.Item key={key} label={label}>
                      {children}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </div>
            </Spin>

            <div style={detailActionBarStyle}>
              <Typography.Text type="secondary" style={{ marginRight: 'auto' }}>
                Send reminders from this page. Counts update after successful delivery request.
              </Typography.Text>
              <Tooltip
                title={emailOnRecord === '' ? 'No email address on this request.' : undefined}
              >
                <span>
                  <Button
                    type="primary"
                    icon={<MailOutlined style={{ color: '#ffffff' }} />}
                    disabled={
                      emailOnRecord === '' ||
                      (detailSource === 'bailiffs' && bailiffDetailLoading)
                    }
                    loading={loadingEmail}
                    style={reminderActionButtonStyle}
                    onClick={() => {
                      void sendReminderEmail()
                    }}
                  >
                    <span style={{ color: '#ffffff' }}>
                      Send email reminder ({detailEmailReminderCount})
                    </span>
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={phoneOnRecord === '' ? 'No phone number on this request.' : undefined}
              >
                <span>
                  <Button
                    type="primary"
                    icon={<MessageOutlined style={{ color: '#ffffff' }} />}
                    disabled={
                      phoneOnRecord === '' ||
                      (detailSource === 'bailiffs' && bailiffDetailLoading)
                    }
                    loading={loadingSms}
                    style={reminderActionButtonStyle}
                    onClick={() => {
                      void sendReminderSms()
                    }}
                  >
                    <span style={{ color: '#ffffff' }}>
                      Send SMS reminder ({detailSmsReminderCount})
                    </span>
                  </Button>
                </span>
              </Tooltip>
            </div>
          </Card>
        )}
      </Space>
    </div>
  )
}

export default PendingRefundReminders
